"use client";

import { useState, useEffect, useMemo } from "react";
import { usePeriodSummary, type PeriodSummary as PeriodSummaryType, type ParticipantRegistrationItem, type PackageParticipantDetail, type InstitutionParticipantDetail } from "@/services/usePeriodSummary";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import { PaidUnpaidChart } from "./PaidUnpaidChart";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
    Download,
    Users,
    Package,
    CheckCircle2,
    XCircle,
    FileSpreadsheet,
    TrendingUp,
    Wallet,
    AlertCircle,
    RefreshCw,
    Loader2,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    Building2,
    Phone,
    Mail,
    Calendar,
    Clock,
    UserCircle2,
    Eye,
    Info,
    Image as ImageIcon,
    TriangleAlert,
    type LucideIcon,
} from "lucide-react";
import { exportPeriodExcel } from "@/services/useExportPeriodExcel";
import { useParticipantInstitutions } from "@/services/adminParticipantService";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE } from "@/constants/options";

type Props = {
    periodId: string;
};

// --- Detail hasil sync payment gateway per-VA ---
interface SyncCheckedDetail {
    customer_number: string | number;
    buyer_name?: string | null;
    participant_count: number;
    previous_status: string;
    new_status?: string | null;
    updated: boolean;
    error?: string | null;
}

interface SyncPaymentResponse {
    message?: string;
    total_va_checked?: number;
    total_participant_updated?: number;
    failed_count?: number;
    failed_customer_numbers?: (string | number)[];
    checked_details?: SyncCheckedDetail[];
}

// --- Persist filter & paginasi daftar peserta per periode ---
// Supaya saat admin berpindah ke tab/halaman lain lalu kembali (yang membuat komponen
// ini remount sepenuhnya), pencarian & filter yang sudah diketik tidak hilang begitu saja.
interface PersistedPeriodFilters {
    search?: string;
    packageFilter?: string;
    statusFilter?: string;
    regTypeFilter?: string;
    institutionFilter?: string;
    genderFilter?: string;
    bloodTypeFilter?: string;
    clothingSizeFilter?: string;
    paymentDateFrom?: string;
    paymentDateTo?: string;
    page?: number;
    perPage?: number;
}

const periodFiltersStorageKey = (periodId: string) => `period-summary-filters:${periodId}`;

function readPersistedPeriodFilters(periodId: string): PersistedPeriodFilters {
    try {
        const raw = sessionStorage.getItem(periodFiltersStorageKey(periodId));
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

// --- Helper: Format Rupiah ---
const formatRupiah = (number: number | string) => {
    const num = typeof number === "string" ? parseFloat(number) : number;
    if (isNaN(num)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
};

// --- Skeleton Loading ---
function PeriodSummarySkeleton() {
    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-3 shadow-sm">
                        <Skeleton className="w-10 h-10 rounded-xl dark:bg-slate-800" />
                        <div className="space-y-2 mt-2">
                            <Skeleton className="h-3 w-20 dark:bg-slate-800" />
                            <Skeleton className="h-6 w-32 dark:bg-slate-800" />
                        </div>
                    </div>
                ))}
            </div>
            <div className="grid lg:grid-cols-7 gap-6">
                <Skeleton className="lg:col-span-4 h-[400px] rounded-3xl dark:bg-slate-900 border border-slate-100 dark:border-slate-800" />
                <Skeleton className="lg:col-span-3 h-[400px] rounded-3xl dark:bg-slate-900 border border-slate-100 dark:border-slate-800" />
            </div>
            <Skeleton className="h-[450px] rounded-3xl dark:bg-slate-900 border border-slate-100 dark:border-slate-800" />
        </div>
    );
}

export function PeriodSummary({ periodId }: Props) {
    const queryClient = useQueryClient();

    // State untuk filter pencarian & paginasi daftar peserta.
    // Diinisialisasi dari sessionStorage (jika ada) supaya tidak reset saat komponen
    // remount akibat berpindah tab/halaman lalu kembali lagi ke halaman ini.
    const [searchInput, setSearchInput] = useState(() => readPersistedPeriodFilters(periodId).search ?? "");
    const [search, setSearch] = useState(() => readPersistedPeriodFilters(periodId).search ?? "");
    const [packageFilter, setPackageFilter] = useState(() => readPersistedPeriodFilters(periodId).packageFilter ?? "all");
    const [statusFilter, setStatusFilter] = useState(() => readPersistedPeriodFilters(periodId).statusFilter ?? "all");
    const [regTypeFilter, setRegTypeFilter] = useState(() => readPersistedPeriodFilters(periodId).regTypeFilter ?? "all");
    const [institutionFilter, setInstitutionFilter] = useState(() => readPersistedPeriodFilters(periodId).institutionFilter ?? "all");
    const [genderFilter, setGenderFilter] = useState(() => readPersistedPeriodFilters(periodId).genderFilter ?? "all");
    const [bloodTypeFilter, setBloodTypeFilter] = useState(() => readPersistedPeriodFilters(periodId).bloodTypeFilter ?? "all");
    const [clothingSizeFilter, setClothingSizeFilter] = useState(() => readPersistedPeriodFilters(periodId).clothingSizeFilter ?? "all");
    // Filter rentang tanggal & jam PEMBAYARAN (bukan tanggal daftar) - format datetime-local
    // ("YYYY-MM-DDTHH:mm"), supaya admin bisa cari data yang baru masuk di rentang waktu
    // tertentu tanpa harus scroll manual saat ada data baru.
    const [paymentDateFrom, setPaymentDateFrom] = useState(() => readPersistedPeriodFilters(periodId).paymentDateFrom ?? "");
    const [paymentDateTo, setPaymentDateTo] = useState(() => readPersistedPeriodFilters(periodId).paymentDateTo ?? "");
    const [page, setPage] = useState(() => readPersistedPeriodFilters(periodId).page ?? 1);
    const [perPage, setPerPage] = useState(() => readPersistedPeriodFilters(periodId).perPage ?? 15);

    // Simpan setiap perubahan filter/paginasi ke sessionStorage (per periode)
    useEffect(() => {
        try {
            sessionStorage.setItem(periodFiltersStorageKey(periodId), JSON.stringify({
                search: searchInput,
                packageFilter,
                statusFilter,
                regTypeFilter,
                institutionFilter,
                genderFilter,
                bloodTypeFilter,
                clothingSizeFilter,
                paymentDateFrom,
                paymentDateTo,
                page,
                perPage,
            } satisfies PersistedPeriodFilters));
        } catch {
            // abaikan jika sessionStorage tidak tersedia (mis. mode private/incognito)
        }
    }, [periodId, searchInput, packageFilter, statusFilter, regTypeFilter, institutionFilter, genderFilter, bloodTypeFilter, clothingSizeFilter, paymentDateFrom, paymentDateTo, page, perPage]);

    const { data: institutionOptions = [] } = useParticipantInstitutions();

    // Pencarian lokal untuk tabel rekap lunas/belum lunas per universitas
    const [institutionReportSearch, setInstitutionReportSearch] = useState("");

    // Hasil sync payment gateway terakhir - ditampilkan sebagai daftar VA & statusnya
    // di dialog, supaya admin bisa lihat persis apa yang terjadi per tagihan.
    const [syncResult, setSyncResult] = useState<SyncPaymentResponse | null>(null);

    // State untuk modal detail partisipan
    const [selectedParticipant, setSelectedParticipant] = useState<ParticipantRegistrationItem | null>(null);
    const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
    const [isLoadingCard, setIsLoadingCard] = useState<boolean>(false);
    const [isDownloadingCard, setIsDownloadingCard] = useState<boolean>(false);
    const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState<boolean>(false);

    // Debounce search agar tidak me-trigger API setiap ketikan
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1); // Reset ke halaman 1 ketika pencarian berubah
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Efek untuk memuat gambar kartu Konaspi ketika modal detail dibuka
    useEffect(() => {
        if (!selectedParticipant?.participant_id || !periodId) {
            setCardImageUrl(null);
            return;
        }

        let isMounted = true;
        const fetchCardPreview = async () => {
            setIsLoadingCard(true);
            try {
                // 1. Dapatkan token publik kartu
                const tokenRes = await axiosClient.get<{ token: string }>(
                    `/public/konvensi/card-token/${selectedParticipant.participant_id}/${periodId}`
                );
                const token = tokenRes.data.token;

                if (!isMounted || !token) return;

                // 2. Dapatkan blob gambar dari token
                const imgRes = await axiosClient.get(
                    `/public/konvensi/card-image/${token}`,
                    { responseType: "blob" }
                );

                if (!isMounted) return;

                const url = window.URL.createObjectURL(new Blob([imgRes.data]));
                setCardImageUrl(url);
            } catch (error) {
                console.error("Gagal memuat pratinjau kartu Konaspi via public token:", error);
                // Fallback: coba endpoint admin/pic/regular
                try {
                    const fallbackRes = await axiosClient.get(
                        `/participants/${selectedParticipant.participant_id}/registrations/period/${periodId}/card-image`,
                        { responseType: "blob" }
                    );
                    if (isMounted) {
                        const url = window.URL.createObjectURL(new Blob([fallbackRes.data]));
                        setCardImageUrl(url);
                    }
                } catch (fallbackErr) {
                    console.error("Gagal memuat fallback kartu:", fallbackErr);
                }
            } finally {
                if (isMounted) setIsLoadingCard(false);
            }
        };

        fetchCardPreview();

        return () => {
            isMounted = false;
        };
    }, [selectedParticipant?.participant_id, periodId]);

    // Bersihkan memory browser dari Object URL saat berubah/unmount
    useEffect(() => {
        return () => {
            if (cardImageUrl) {
                window.URL.revokeObjectURL(cardImageUrl);
            }
        };
    }, [cardImageUrl]);

    // Fungsi unduh kartu peserta (JPEG)
    const handleDownloadCardImage = () => {
        if (!cardImageUrl || !selectedParticipant) return;
        setIsDownloadingCard(true);
        try {
            const link = document.createElement("a");
            link.href = cardImageUrl;
            const regNum = selectedParticipant.registration_number || `Reg_${selectedParticipant.registration_id}`;
            const cleanName = (selectedParticipant.full_name || selectedParticipant.participant?.full_name || "Peserta").replace(/\s+/g, "_");
            link.setAttribute("download", `ID_Card_KONASPI_${regNum}_${cleanName}.jpeg`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Kartu Konaspi berhasil diunduh!");
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Gagal mengunduh kartu Konaspi.");
        } finally {
            setIsDownloadingCard(false);
        }
    };

    // Fungsi unduh rekap lunas/belum lunas per universitas (CSV) — untuk laporan cepat ke pimpinan
    const handleExportInstitutionCsv = () => {
        if (!filteredInstitutionsDistribution.length) {
            toast.error("Belum ada data universitas untuk diunduh.");
            return;
        }
        const rows = [
            ["Universitas / Institusi", "Total Peserta", "Lunas", "Belum Lunas", "Status"],
            ...filteredInstitutionsDistribution.map((inst: InstitutionParticipantDetail) => {
                const paidC = parseInt(inst.paid || "0");
                const unpaidC = parseInt(inst.unpaid || "0");
                return [
                    inst.institution,
                    String(inst.total),
                    String(paidC),
                    String(unpaidC),
                    unpaidC === 0 ? "Lunas Semua" : "Belum Lunas Semua",
                ];
            }),
        ];
        const csvContent = rows
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `rekap-universitas-periode-${periodId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Rekap per universitas berhasil diunduh.");
    };

    // Fungsi cek & sinkronisasi status pembayaran per partisipan
    const handleCheckParticipantPaymentStatus = async () => {
        if (!selectedParticipant) return;
        setIsCheckingPaymentStatus(true);
        try {
            let paymentId = selectedParticipant?.latest_payment?.payment_id;

            // Jika payment_id belum ada di state latest_payment, cari dari API list payment by registration_id
            if (!paymentId) {
                const resList = await axiosClient.get(`/payments/registrations/${selectedParticipant.registration_id}`);
                const payments = resList.data?.data;
                if (Array.isArray(payments) && payments.length > 0) {
                    paymentId = payments[0].payment_id;
                }
            }

            if (!paymentId) {
                toast.error("Belum ada data tagihan / Virtual Account yang dibuat untuk peserta ini.");
                setIsCheckingPaymentStatus(false);
                return;
            }

            const { data } = await axiosClient.get<{ status?: string; message?: string }>(
                `/payments/sync-status/${paymentId}`
            );

            const newStatus = data.status?.toLowerCase();
            if (newStatus === "paid") {
                toast.success(data.message || "Status berhasil disinkronkan: LUNAS (PAID)!");
            } else if (newStatus === "unpaid" || newStatus === "pending") {
                toast(data.message || `Status dari Bank: ${data.status?.toUpperCase() || "BELUM LUNAS"}`, { icon: 'ℹ️' });
            } else {
                toast.success(data.message || "Status pembayaran berhasil diperiksa.");
            }

            // Update state lokal agar badge langsung berubah di modal tanpa perlu tutup & buka lagi
            if (newStatus) {
                setSelectedParticipant((prev) => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        payment_status: newStatus,
                        latest_payment: prev.latest_payment
                            ? { ...prev.latest_payment, status: newStatus }
                            : { payment_id: paymentId, status: newStatus }
                    };
                });
            }

            // Refresh data summary/list di latar belakang
            queryClient.invalidateQueries({ queryKey: ["period-summary", periodId] });
        } catch (error) {
            let errorMessage = "Gagal memeriksa status pembayaran ke server Bank/Payment Gateway.";
            if (isAxiosError(error) && error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            console.error("Check status error:", error);
            toast.error(errorMessage);
        } finally {
            setIsCheckingPaymentStatus(false);
        }
    };

    // Query untuk mengambil data summary sekaligus daftar peserta
    const { data, isLoading, isFetching } = usePeriodSummary(periodId, {
        search,
        package_id: packageFilter,
        payment_status: statusFilter,
        registration_type: regTypeFilter,
        institution: institutionFilter,
        gender: genderFilter,
        blood_type: bloodTypeFilter,
        clothing_size: clothingSizeFilter,
        payment_date_from: paymentDateFrom,
        payment_date_to: paymentDateTo,
        page,
        per_page: perPage,
    });

    const summaryData = data as PeriodSummaryType | undefined;

    // Mendukung struktur respons baik yang langsung di root maupun di dalam properti "summary"
    const metrics = summaryData?.summary || summaryData;
    const institutionsDistribution = metrics?.participants_per_institution ?? [];

    // Tabel rekap per universitas: urutkan berdasarkan jumlah belum lunas terbanyak dulu
    // (supaya pimpinan langsung lihat universitas mana yang perlu ditagih), lalu bisa dicari.
    // Hook ini HARUS ditempatkan sebelum early-return loading di bawah, supaya urutan hook
    // yang di-render tetap konsisten antar render (Rules of Hooks).
    const filteredInstitutionsDistribution = useMemo(() => {
        const keyword = institutionReportSearch.trim().toLowerCase();
        const list = keyword
            ? institutionsDistribution.filter((inst: InstitutionParticipantDetail) =>
                inst.institution?.toLowerCase().includes(keyword)
            )
            : institutionsDistribution;

        return [...list].sort((a: InstitutionParticipantDetail, b: InstitutionParticipantDetail) => {
            const unpaidDiff = parseInt(b.unpaid || "0") - parseInt(a.unpaid || "0");
            if (unpaidDiff !== 0) return unpaidDiff;
            return (b.total || 0) - (a.total || 0);
        });
    }, [institutionsDistribution, institutionReportSearch]);

    // Total baris "Grand Total" di bawah tabel universitas (mengikuti pencarian aktif),
    // seperti baris total pada pivot table Excel.
    const institutionsGrandTotal = useMemo(() => {
        return filteredInstitutionsDistribution.reduce(
            (acc: { total: number; paid: number; unpaid: number }, inst: InstitutionParticipantDetail) => {
                acc.total += inst.total || 0;
                acc.paid += parseInt(inst.paid || "0");
                acc.unpaid += parseInt(inst.unpaid || "0");
                return acc;
            },
            { total: 0, paid: 0, unpaid: 0 }
        );
    }, [filteredInstitutionsDistribution]);

    // --- MUTASI SINKRONISASI PEMBAYARAN ---
    const syncMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosClient.get<SyncPaymentResponse>(`/periods/${periodId}/payment/sync-status`);
            return response.data;
        },
        onSuccess: (res) => {
            const failedCount = res.failed_count ?? 0;

            // Toast singkat sebagai notifikasi cepat...
            if (failedCount > 0) {
                toast(`${res.message || "Sinkronisasi selesai"} - buka detail untuk lihat VA yang gagal.`, { icon: <TriangleAlert className="w-5 h-5 text-amber-500" />, duration: 6000 });
            } else {
                toast.success(res.message || "Sinkronisasi Selesai");
            }

            // ...dan daftar lengkap VA yang dicek + statusnya ditampilkan di dialog detail,
            // supaya admin bisa lihat persis apa yang terjadi per tagihan, bukan cuma angka.
            setSyncResult(res);

            // Auto-refresh data
            queryClient.invalidateQueries({ queryKey: ["period-summary", periodId] });
        },
        onError: (err: unknown) => {
            let errorMessage = "Gagal melakukan sinkronisasi pembayaran dari Payment Gateway.";

            if (isAxiosError(err) && err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            console.error("Sync Error:", err);
            toast.error(errorMessage);
        }
    });

    if (isLoading && !summaryData) return <PeriodSummarySkeleton />;

    const totalParticipants = metrics?.total_participants ?? 0;
    const totalBiaya = metrics?.total_biaya_keseluruhan ?? 0;
    const totalPaid = metrics?.total_biaya_paid ?? 0;
    const totalUnpaid = metrics?.total_biaya_unpaid ?? 0;
    const paidCount = metrics?.paid ?? 0;
    const unpaidCount = metrics?.unpaid ?? 0;
    const packagesDistribution = metrics?.participants_per_package ?? [];
    const totalInstitutions = metrics?.total_institutions ?? institutionsDistribution.length;
    const fullyPaidInstitutionsCount = metrics?.fully_paid_institutions ?? 0;
    const notFullyPaidInstitutionsCount = metrics?.not_fully_paid_institutions ?? Math.max(totalInstitutions - fullyPaidInstitutionsCount, 0);

    // Daftar peserta dan informasi paginasi
    const participantsList = summaryData?.data || summaryData?.participants || [];
    const meta = summaryData?.meta;

    const isFiltering = Boolean(
        search ||
        packageFilter !== "all" ||
        statusFilter !== "all" ||
        regTypeFilter !== "all" ||
        institutionFilter !== "all" ||
        genderFilter !== "all" ||
        bloodTypeFilter !== "all" ||
        clothingSizeFilter !== "all" ||
        paymentDateFrom ||
        paymentDateTo
    );

    const resetAllFilters = () => {
        setSearchInput("");
        setSearch("");
        setPackageFilter("all");
        setStatusFilter("all");
        setRegTypeFilter("all");
        setInstitutionFilter("all");
        setGenderFilter("all");
        setBloodTypeFilter("all");
        setClothingSizeFilter("all");
        setPaymentDateFrom("");
        setPaymentDateTo("");
        setPage(1);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">

            {/* --- Section 1: Key Metrics --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Total Peserta"
                    value={totalParticipants}
                    icon={Users}
                    colorClass="blue"
                />
                <StatCard
                    label="Total Tagihan"
                    value={totalBiaya}
                    isCurrency
                    icon={Wallet}
                    colorClass="sky"
                />
                <StatCard
                    label="Pemasukan (Lunas)"
                    value={totalPaid}
                    isCurrency
                    icon={CheckCircle2}
                    colorClass="emerald"
                />
                <StatCard
                    label="Belum Lunas"
                    value={totalUnpaid}
                    isCurrency
                    icon={AlertCircle}
                    colorClass="rose"
                />
            </div>

            {/* --- Section 2: Chart & Package Distribution --- */}
            <div className="grid lg:grid-cols-7 gap-6">

                {/* Kolom Kiri: Chart (Span 4) */}
                <div className="lg:col-span-4 bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-blue-600" /> Rasio Pembayaran
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">Komposisi peserta lunas vs belum lunas (Orang)</p>
                        </div>
                    </div>
                    <div className="p-6 flex-1 flex items-center justify-center min-h-[300px]">
                        <PaidUnpaidChart
                            paid={paidCount}
                            unpaid={unpaidCount}
                        />
                    </div>
                </div>

                {/* Kolom Kanan: Distribusi & Panel Aksi (Span 3) */}
                <div className="lg:col-span-3 flex flex-col gap-6">

                    {/* ACTION PANEL (Export & Sync) */}
                    <div className="bg-slate-900 dark:bg-slate-800 p-6 rounded-3xl shadow-xl relative overflow-hidden group">
                        <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" />

                        <div className="relative z-10 flex items-center gap-3 mb-4">
                            <div className="p-2 bg-white/10 rounded-xl">
                                <FileSpreadsheet className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="font-bold text-white">Laporan & Sinkronisasi</h3>
                        </div>

                        <p className="text-xs text-slate-300 mb-6 leading-relaxed">
                            Unduh data lengkap untuk pelaporan atau lakukan pengecekan ulang (sync) ke Payment Gateway jika ada pembayaran yang tertunda.
                        </p>

                        <div className="flex flex-col gap-3">
                            {/* Tombol Export */}
                            <Button
                                className="w-full justify-center h-11 bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-bold shadow-md"
                                onClick={() => exportPeriodExcel(periodId, {
                                    search: searchInput,
                                    package_id: packageFilter,
                                    status: statusFilter,
                                    registration_type: regTypeFilter,
                                    institution: institutionFilter,
                                    gender: genderFilter,
                                    blood_type: bloodTypeFilter,
                                    clothing_size: clothingSizeFilter,
                                    payment_date_from: paymentDateFrom,
                                    payment_date_to: paymentDateTo,
                                })}
                            >
                                <Download className="mr-2 h-4 w-4" /> Download Excel ({participantsList.length} di halaman, sesuai filter)
                            </Button>

                            {/* TOMBOL SYNC */}
                            <Button
                                variant="outline"
                                className="w-full justify-center h-11 border-slate-700 bg-transparent text-white hover:bg-slate-800 hover:text-white rounded-xl font-bold transition-all duration-300"
                                onClick={() => syncMutation.mutate()}
                                disabled={syncMutation.isPending}
                            >
                                {syncMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-400" />
                                        Menyinkronkan Data...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4 text-blue-400" />
                                        Sync Payment Gateway
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Distribusi Paket */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col flex-1 overflow-hidden">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                                <Package className="w-5 h-5 text-amber-500" /> Distribusi Paket
                            </h3>
                        </div>

                        <div className="p-6 flex-1 space-y-6 max-h-[400px] overflow-y-auto">
                            {packagesDistribution?.map((pkg: PackageParticipantDetail, idx: number) => {
                                const percentage = totalParticipants > 0
                                    ? Math.round((pkg.total / totalParticipants) * 100)
                                    : 0;

                                const paidCountPkg = parseInt(pkg.paid || "0");
                                const unpaidCountPkg = parseInt(pkg.unpaid || "0");

                                return (
                                    <div key={idx} className="space-y-2">
                                        <div className="flex items-start justify-between text-sm gap-4">
                                            <span className="font-bold text-slate-700 dark:text-slate-200 leading-snug">
                                                {pkg.package_name}
                                            </span>
                                            <span className="font-black text-slate-900 dark:text-white shrink-0">
                                                {pkg.total} <span className="text-[10px] font-bold text-slate-400 ml-0.5">({percentage}%)</span>
                                            </span>
                                        </div>

                                        <Progress value={percentage} className="h-2 bg-slate-100 dark:bg-slate-800" />

                                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
                                            <span className={paidCountPkg > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"}>
                                                Lunas: {paidCountPkg}
                                            </span>
                                            <span className={unpaidCountPkg > 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-400"}>
                                                Belum: {unpaidCountPkg}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}

                            {(!packagesDistribution || packagesDistribution.length === 0) && (
                                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                                    <XCircle className="w-12 h-12 mb-3 opacity-20" />
                                    <p className="text-sm font-medium">Belum ada data paket.</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* --- Section 2b: Rekap Lunas/Belum Lunas per Universitas --- */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Rekap Pembayaran per Universitas
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Jumlah universitas/institusi yang sudah lunas semua dan yang masih ada peserta belum lunas, untuk laporan cepat ke pimpinan.
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-3.5 rounded-xl border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 font-bold flex items-center gap-2 shadow-sm transition-all self-start sm:self-auto shrink-0"
                            onClick={handleExportInstitutionCsv}
                        >
                            <FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <span>Unduh Rekap Universitas (CSV)</span>
                        </Button>
                    </div>

                    {/* Mini stat: total universitas, lunas semua, belum lunas semua */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3">
                            <div className="p-2 rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 shrink-0">
                                <Building2 className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Total Universitas</div>
                                <div className="text-lg font-black text-slate-900 dark:text-white">{totalInstitutions}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3">
                            <div className="p-2 rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 shrink-0">
                                <CheckCircle2 className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Lunas Semua</div>
                                <div className="text-lg font-black text-emerald-600 dark:text-emerald-400">{fullyPaidInstitutionsCount}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3">
                            <div className="p-2 rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 shrink-0">
                                <Clock className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Belum Lunas Semua</div>
                                <div className="text-lg font-black text-rose-600 dark:text-rose-400">{notFullyPaidInstitutionsCount}</div>
                            </div>
                        </div>
                    </div>

                    {/* Search khusus tabel universitas */}
                    <div className="relative w-full sm:max-w-sm">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Cari nama universitas..."
                            value={institutionReportSearch}
                            onChange={(e) => setInstitutionReportSearch(e.target.value)}
                            className="pl-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm focus:bg-white dark:focus:bg-slate-800 transition-all"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                <th className="py-3.5 px-6 w-12">No</th>
                                <th className="py-3.5 px-6">Universitas / Institusi</th>
                                <th className="py-3.5 px-6 text-center">Total Peserta</th>
                                <th className="py-3.5 px-6 text-center">Lunas</th>
                                <th className="py-3.5 px-6 text-center">Belum Lunas</th>
                                <th className="py-3.5 px-6 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-sm">
                            {filteredInstitutionsDistribution.length > 0 ? (
                                filteredInstitutionsDistribution.map((inst: InstitutionParticipantDetail, idx: number) => {
                                    const paidC = parseInt(inst.paid || "0");
                                    const unpaidC = parseInt(inst.unpaid || "0");
                                    const isFullyPaid = unpaidC === 0;

                                    return (
                                        <tr key={inst.institution} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                            <td className="py-3.5 px-6 font-bold text-slate-500 dark:text-slate-400">{idx + 1}</td>
                                            <td className="py-3.5 px-6 font-semibold text-slate-800 dark:text-slate-200">
                                                {inst.institution}
                                            </td>
                                            <td className="py-3.5 px-6 text-center font-bold text-slate-700 dark:text-slate-300">{inst.total}</td>
                                            <td className="py-3.5 px-6 text-center font-bold text-emerald-600 dark:text-emerald-400">{paidC}</td>
                                            <td className="py-3.5 px-6 text-center font-bold text-rose-600 dark:text-rose-400">{unpaidC}</td>
                                            <td className="py-3.5 px-6 text-center">
                                                {isFullyPaid ? (
                                                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-bold px-2.5 py-0.5 shadow-none">
                                                        Lunas Semua
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800 font-bold px-2.5 py-0.5 shadow-none">
                                                        Belum Lunas Semua
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center max-w-sm mx-auto text-slate-400">
                                            <Building2 className="w-10 h-10 mb-3 opacity-20" />
                                            <p className="text-sm font-medium">
                                                {institutionReportSearch ? "Universitas tidak ditemukan." : "Belum ada data universitas."}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {filteredInstitutionsDistribution.length > 0 && (
                            <tfoot className="sticky bottom-0 z-10">
                                <tr className="bg-slate-100/95 dark:bg-slate-800 backdrop-blur border-t-2 border-slate-300 dark:border-slate-700 text-sm font-black text-slate-900 dark:text-white">
                                    <td className="py-3.5 px-6" colSpan={2}>
                                        Grand Total ({filteredInstitutionsDistribution.length} universitas)
                                    </td>
                                    <td className="py-3.5 px-6 text-center">{institutionsGrandTotal.total}</td>
                                    <td className="py-3.5 px-6 text-center text-emerald-700 dark:text-emerald-400">{institutionsGrandTotal.paid}</td>
                                    <td className="py-3.5 px-6 text-center text-rose-700 dark:text-rose-400">{institutionsGrandTotal.unpaid}</td>
                                    <td className="py-3.5 px-6"></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            {/* --- Section 3: Daftar Peserta (Dengan Filter & Paginasi) --- */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                
                {/* Header & Filter Bar */}
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                                <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Daftar Peserta & Pembayaran
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Menampilkan data peserta yang mendaftar pada periode ini. Gunakan filter di bawah untuk memilah data.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 self-start sm:self-auto flex-wrap">
                            {isFetching && (
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full animate-pulse">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Memperbarui data...
                                </div>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 px-3.5 rounded-xl border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 font-bold flex items-center gap-2 shadow-sm transition-all"
                                onClick={() => exportPeriodExcel(periodId, {
                                    search: searchInput,
                                    package_id: packageFilter,
                                    status: statusFilter,
                                    registration_type: regTypeFilter,
                                    institution: institutionFilter,
                                    gender: genderFilter,
                                    blood_type: bloodTypeFilter,
                                    clothing_size: clothingSizeFilter,
                                    payment_date_from: paymentDateFrom,
                                    payment_date_to: paymentDateTo,
                                })}
                            >
                                <FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                <span>Export Excel Terfilter ({meta?.total || participantsList.length})</span>
                            </Button>
                        </div>
                    </div>

                    {/* Filter Controls */}
                    <div className="space-y-3 pt-2">
                        {/* Search Input (baris sendiri, full width) */}
                        <div className="relative w-full">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder="Cari nama, email, institusi, atau no reg..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="pl-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm focus:bg-white dark:focus:bg-slate-800 transition-all"
                            />
                        </div>

                        {/* Baris filter: wrap otomatis dengan lebar tetap per dropdown supaya proporsional */}
                        <div className="flex flex-wrap items-center gap-2">
                        {/* Filter Paket */}
                        <Select
                            value={packageFilter}
                            onValueChange={(val) => {
                                setPackageFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Semua Paket" />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="max-w-[90vw] sm:max-w-md md:max-w-lg">
                                <SelectItem value="all" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Package className="w-4 h-4 text-amber-500 shrink-0" />
                                        <span>Semua Paket</span>
                                    </div>
                                </SelectItem>
                                {packagesDistribution?.map((pkg: PackageParticipantDetail) => (
                                    <SelectItem key={pkg.package_id} value={pkg.package_id.toString()} className="py-2.5 px-3 cursor-pointer">
                                        <div className="flex items-center gap-2 min-w-0 w-full pr-2">
                                            <Package className="w-4 h-4 text-slate-400 shrink-0" />
                                            <span className="truncate font-medium text-slate-700 dark:text-slate-200" title={pkg.package_name}>
                                                {pkg.package_name}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Filter Tipe Registrasi */}
                        <Select
                            value={regTypeFilter}
                            onValueChange={(val) => {
                                setRegTypeFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Semua Tipe" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-indigo-500 shrink-0" />
                                        <span>Semua Tipe</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="personal" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <UserCircle2 className="w-4 h-4 text-sky-500 shrink-0" />
                                        <span>Mandiri (Personal)</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="kolektif" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-purple-500 shrink-0" />
                                        <span>Kolektif (Import PIC)</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Filter Status Pembayaran */}
                        <Select
                            value={statusFilter}
                            onValueChange={(val) => {
                                setStatusFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Status Pembayaran" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Wallet className="w-4 h-4 text-blue-500 shrink-0" />
                                        <span>Semua Status</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="paid" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                        <span>Lunas (Paid)</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="unpaid" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-rose-500 shrink-0" />
                                        <span>Belum Lunas (Unpaid)</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Filter Universitas / Institusi */}
                        <Select
                            value={institutionFilter}
                            onValueChange={(val) => {
                                setInstitutionFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Semua Universitas" />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="max-w-[90vw] sm:max-w-md md:max-w-lg">
                                <SelectItem value="all" className="py-2.5 px-3">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                                        <span>Semua Universitas</span>
                                    </div>
                                </SelectItem>
                                {institutionOptions.map((inst) => (
                                    <SelectItem key={inst} value={inst} className="py-2.5 px-3">
                                        <span className="truncate block max-w-[260px]" title={inst}>{inst}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Filter Jenis Kelamin */}
                        <Select
                            value={genderFilter}
                            onValueChange={(val) => {
                                setGenderFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Jenis Kelamin" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-2.5 px-3">Semua Gender</SelectItem>
                                <SelectItem value="male" className="py-2.5 px-3">Laki-laki</SelectItem>
                                <SelectItem value="female" className="py-2.5 px-3">Perempuan</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Filter Golongan Darah */}
                        <Select
                            value={bloodTypeFilter}
                            onValueChange={(val) => {
                                setBloodTypeFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Golongan Darah" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-2.5 px-3">Semua Gol. Darah</SelectItem>
                                {ITEMS_BLOOD_TYPE.map((item) => (
                                    <SelectItem key={item.value} value={item.value} className="py-2.5 px-3">{item.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Filter Ukuran Baju */}
                        <Select
                            value={clothingSizeFilter}
                            onValueChange={(val) => {
                                setClothingSizeFilter(val);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm font-medium w-[190px]">
                                <div className="truncate w-full pr-1">
                                    <SelectValue placeholder="Ukuran Baju" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-2.5 px-3">Semua Ukuran</SelectItem>
                                {ITEMS_CLOTHES_SIZE.map((item) => (
                                    <SelectItem key={item.value} value={item.value} className="py-2.5 px-3">{item.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Filter Rentang Tanggal & Jam Pembayaran - untuk cari data yang baru
                            masuk di rentang waktu tertentu (bukan tanggal daftar/registrasi) */}
                        <div className="flex items-center gap-1.5 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 px-3">
                            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <input
                                type="datetime-local"
                                value={paymentDateFrom}
                                onChange={(e) => {
                                    setPaymentDateFrom(e.target.value);
                                    setPage(1);
                                }}
                                title="Tanggal & jam bayar dari"
                                className="bg-transparent text-xs font-medium text-slate-700 dark:text-slate-200 outline-none w-[150px] [color-scheme:light] dark:[color-scheme:dark]"
                            />
                            <span className="text-slate-400 text-xs">-</span>
                            <input
                                type="datetime-local"
                                value={paymentDateTo}
                                onChange={(e) => {
                                    setPaymentDateTo(e.target.value);
                                    setPage(1);
                                }}
                                title="Tanggal & jam bayar sampai"
                                className="bg-transparent text-xs font-medium text-slate-700 dark:text-slate-200 outline-none w-[150px] [color-scheme:light] dark:[color-scheme:dark]"
                            />
                        </div>

                        {/* Tombol Reset Filter, hanya tampil jika ada filter/pencarian aktif */}
                        {isFiltering && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-10 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                                onClick={resetAllFilters}
                            >
                                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reset Filter
                            </Button>
                        )}
                        </div>
                    </div>
                </div>

                {/* Table Data */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                <th className="py-3.5 px-6 w-16">No</th>
                                <th className="py-3.5 px-6">Identitas Peserta</th>
                                <th className="py-3.5 px-6">Paket Pilihan</th>
                                <th className="py-3.5 px-6 text-center">Tipe</th>
                                <th className="py-3.5 px-6 text-center">Status Pembayaran</th>
                                <th className="py-3.5 px-6">Tanggal Daftar</th>
                                <th className="py-3.5 px-6 text-center sticky right-0 z-10 bg-slate-50 dark:bg-slate-800 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-sm">
                            {participantsList.length > 0 ? (
                                participantsList.map((reg: ParticipantRegistrationItem, idx: number) => {
                                    const itemIdx = meta ? (meta.current_page - 1) * meta.per_page + idx + 1 : idx + 1;
                                    const fullName = reg.full_name || reg.participant?.full_name || "-";
                                    const email = reg.email || reg.participant?.email || "-";
                                    const phone = reg.phone_mobile || reg.participant?.phone_mobile;
                                    const institution = reg.institution || reg.participant?.institution;
                                    const pkgName = reg.package_name || reg.package?.name || "-";
                                    const pkgPrice = reg.package_price || reg.package?.price || 0;
                                    const regType = reg.registration_type || reg.participant?.registration_type || "personal";
                                    
                                    // Penentuan status lunas dari payment_status atau latest_payment
                                    const isPaid = reg.payment_status === "paid" || reg.latest_payment?.status === "paid";
                                    const invoiceNo = reg.invoice_number || reg.latest_payment?.invoice_number;

                                    return (
                                        <tr 
                                            key={reg.registration_id} 
                                            className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors group"
                                        >
                                            {/* No & Reg Number */}
                                            <td className="py-4 px-6 align-top">
                                                <div className="font-bold text-slate-700 dark:text-slate-300">
                                                    {itemIdx}
                                                </div>
                                                <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                                    #{reg.registration_id}
                                                </div>
                                            </td>

                                            {/* Identitas Peserta */}
                                            <td className="py-4 px-6 align-top space-y-1.5">
                                                <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                                                    <UserCircle2 className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span>{fullName}</span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5 text-slate-400" /> {email}
                                                    </span>
                                                    {phone && (
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="w-3.5 h-3.5 text-slate-400" /> {phone}
                                                        </span>
                                                    )}
                                                </div>
                                                {institution && (
                                                    <div className="flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                                                        <Building2 className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                        <span>{institution}</span>
                                                    </div>
                                                )}
                                                <div className="text-[10px] font-mono font-bold text-slate-400 pt-0.5">
                                                    No Reg: {reg.registration_number}
                                                </div>
                                            </td>

                                            {/* Paket Pilihan */}
                                            <td className="py-4 px-6 align-top">
                                                <div className="font-bold text-slate-800 dark:text-slate-200 text-xs sm:text-sm leading-snug">
                                                    {pkgName}
                                                </div>
                                                <div className="mt-1.5">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200/50 dark:border-amber-800/50">
                                                        {formatRupiah(pkgPrice)}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Tipe Registrasi */}
                                            <td className="py-4 px-6 align-top text-center">
                                                {regType === "kolektif" ? (
                                                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800 font-bold px-2.5 py-0.5 shadow-none">
                                                        Kolektif
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-800 font-bold px-2.5 py-0.5 shadow-none">
                                                        Personal
                                                    </Badge>
                                                )}
                                            </td>

                                            {/* Status Pembayaran */}
                                            <td className="py-4 px-6 align-top text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    {isPaid ? (
                                                        <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-bold px-3 py-1 shadow-none flex items-center gap-1.5">
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> Lunas
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800 font-bold px-3 py-1 shadow-none flex items-center gap-1.5">
                                                            <Clock className="w-3.5 h-3.5" /> Belum Lunas
                                                        </Badge>
                                                    )}
                                                    {invoiceNo && (
                                                        <span className="text-[10px] font-mono text-slate-400 mt-0.5">
                                                            INV: {invoiceNo}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Tanggal Daftar */}
                                            <td className="py-4 px-6 align-top">
                                                <div className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                                    <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                    <span>{reg.registration_date?.split(" ")[0]}</span>
                                                </div>
                                                <div className="text-[11px] text-slate-400 mt-0.5 pl-5 font-mono">
                                                    {reg.registration_date?.split(" ")[1] || ""}
                                                </div>
                                            </td>

                                            {/* Aksi / Detail (Sticky right) */}
                                            <td className="py-4 px-6 align-top text-center sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/80 dark:group-hover:bg-slate-800/40 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setSelectedParticipant(reg)}
                                                    className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-900/50 dark:hover:bg-blue-900/30 font-bold gap-1.5 shadow-xs"
                                                >
                                                    <Eye className="w-4 h-4" /> Detail
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center max-w-sm mx-auto text-slate-400">
                                            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                                                <Filter className="w-6 h-6 text-slate-400" />
                                            </div>
                                            <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-1">
                                                Peserta Tidak Ditemukan
                                            </h4>
                                            <p className="text-xs text-slate-500 leading-relaxed">
                                                Tidak ada data peserta yang cocok dengan filter atau kata kunci pencarian Anda. Coba atur ulang filter.
                                            </p>
                                            {isFiltering && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="mt-4 rounded-xl text-xs font-bold"
                                                    onClick={resetAllFilters}
                                                >
                                                    Reset Semua Filter
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Paginasi */}
                {meta && meta.total > 0 && (
                    <div className="p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-medium text-slate-600 dark:text-slate-400">
                        <div className="flex items-center gap-2">
                            <span>Menampilkan</span>
                            <span className="font-bold text-slate-900 dark:text-white">
                                {meta.from || 0} - {meta.to || 0}
                            </span>
                            <span>dari</span>
                            <span className="font-bold text-slate-900 dark:text-white">
                                {meta.total}
                            </span>
                            <span>data</span>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-3">
                            <div className="flex items-center gap-1.5 mr-2">
                                <span className="text-slate-400">Per halaman:</span>
                                <Select
                                    value={perPage.toString()}
                                    onValueChange={(val) => {
                                        setPerPage(Number(val));
                                        setPage(1);
                                    }}
                                >
                                    <SelectTrigger className="h-8 w-16 rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-xs font-bold">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">10</SelectItem>
                                        <SelectItem value="15">15</SelectItem>
                                        <SelectItem value="25">25</SelectItem>
                                        <SelectItem value="50">50</SelectItem>
                                        <SelectItem value="100">100</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 px-3 rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 font-bold"
                                    disabled={meta.current_page <= 1 || isFetching}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                                </Button>
                                <span className="px-2 font-bold text-slate-800 dark:text-slate-200">
                                    {meta.current_page} / {meta.last_page || 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 px-3 rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 font-bold"
                                    disabled={meta.current_page >= meta.last_page || isFetching}
                                    onClick={() => setPage((prev) => Math.min(meta.last_page, prev + 1))}
                                >
                                    Next <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* --- MODAL DETAIL PARTICIPANT --- */}
            <Dialog open={!!selectedParticipant} onOpenChange={(open) => !open && setSelectedParticipant(null)}>
                <DialogContent className="!max-w-[96vw] sm:!max-w-[92vw] lg:!max-w-[1150px] xl:!max-w-[1280px] w-full max-h-[92vh] overflow-hidden flex flex-col rounded-[2rem] p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
                    {/* Header */}
                    <DialogHeader className="shrink-0 p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-left">
                        {/* Sisi Kiri: Nama Partisipan & Waktu Daftar */}
                        <div className="flex items-start gap-3.5 min-w-0">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-200/60 dark:border-indigo-800/60 shadow-sm">
                                <UserCircle2 className="w-7 h-7" />
                            </div>
                            <div className="min-w-0">
                                <DialogTitle className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight truncate">
                                    {selectedParticipant?.full_name || selectedParticipant?.participant?.full_name || "-"}
                                </DialogTitle>
                                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    <span>Didaftarkan pada: <strong className="font-semibold text-slate-700 dark:text-slate-300">{selectedParticipant?.registration_date || "-"}</strong></span>
                                </div>
                            </div>
                        </div>

                        {/* Sisi Kanan: Informasi Lain (Badges & Tipe Pendaftaran) */}
                        <div className="flex flex-col sm:items-end gap-2 shrink-0 sm:text-right border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-200/60 dark:border-slate-700/60">
                            <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                                <Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 font-mono font-bold px-3 py-1 text-xs shadow-none">
                                    #{selectedParticipant?.registration_id}
                                </Badge>
                                <Badge className="bg-slate-200/80 text-slate-800 dark:bg-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 font-mono font-bold px-3 py-1 text-xs shadow-none">
                                    No Reg: {selectedParticipant?.registration_number}
                                </Badge>
                                {selectedParticipant?.registration_type === "kolektif" ? (
                                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800 font-bold px-3 py-1 text-xs shadow-none">
                                        Kolektif
                                    </Badge>
                                ) : (
                                    <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-800 font-bold px-3 py-1 text-xs shadow-none">
                                        Personal
                                    </Badge>
                                )}
                            </div>
                            {(selectedParticipant?.package_name || selectedParticipant?.package?.name) && (
                                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center sm:justify-end gap-1.5 truncate max-w-[280px] sm:max-w-[380px]">
                                    <Package className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                    <span>{selectedParticipant.package_name || selectedParticipant.package?.name}</span>
                                </p>
                            )}
                        </div>
                    </DialogHeader>

                    {/* Content Body (Scrollable 2 Columns Layout) */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-8 flex flex-col lg:flex-row gap-8 items-start w-full min-h-0">
                        {/* Kolom Kiri: Kartu Konaspi & Tombol Download */}
                        <div className="w-full lg:w-[320px] shrink-0 space-y-4 mx-auto lg:mx-0">
                            <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[2rem] shadow-xl">
                                {isLoadingCard ? (
                                    <div className="w-full aspect-[9/16] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 animate-pulse">
                                        <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Memuat Kartu Konaspi...</span>
                                    </div>
                                ) : cardImageUrl ? (
                                    <img
                                        src={cardImageUrl}
                                        alt="ID Card KONASPI"
                                        className="w-full aspect-[9/16] object-cover rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none shadow-sm"
                                    />
                                ) : (
                                    <div className="w-full aspect-[9/16] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 p-6 text-center">
                                        <ImageIcon className="w-10 h-10 opacity-40" />
                                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                            Kartu belum siap atau gambar tidak dapat dimuat.
                                        </span>
                                    </div>
                                )}
                            </div>

                            <Button
                                onClick={handleDownloadCardImage}
                                disabled={isDownloadingCard || !cardImageUrl || isLoadingCard}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-12 rounded-2xl shadow-lg shadow-indigo-500/25 transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                {isDownloadingCard ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                <span>Unduh Kartu (JPEG)</span>
                            </Button>
                        </div>

                        {/* Kolom Kanan: Detail Informasi Partisipan */}
                        <div className="flex-1 min-w-0 w-full space-y-6">
                            {/* Status & Tagihan Banner */}
                            <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block">Status Pembayaran</span>
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        {(selectedParticipant?.payment_status === "paid" || selectedParticipant?.latest_payment?.status === "paid") ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-black px-3 py-1 shadow-none flex items-center gap-1.5 text-sm">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> LUNAS (PAID)
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800 font-black px-3 py-1 shadow-none flex items-center gap-1.5 text-sm">
                                                <Clock className="w-4 h-4 text-rose-600 dark:text-rose-400" /> BELUM LUNAS (UNPAID)
                                            </Badge>
                                        )}

                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={handleCheckParticipantPaymentStatus}
                                            disabled={isCheckingPaymentStatus}
                                            className="h-8 px-3 rounded-xl text-xs font-bold bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-400 transition-all flex items-center gap-1.5 shadow-sm"
                                            title="Cek langsung status pembayaran ke server Bank/Payment Gateway"
                                        >
                                            {isCheckingPaymentStatus ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                                            ) : (
                                                <RefreshCw className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                            )}
                                            <span>Cek Status Bayar</span>
                                        </Button>
                                    </div>
                                </div>

                                <div className="sm:text-right space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block">Total Tagihan</span>
                                    <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                                        {formatRupiah(selectedParticipant?.package_price ?? selectedParticipant?.package?.price ?? 0)}
                                    </p>
                                    {(selectedParticipant?.invoice_number || selectedParticipant?.latest_payment?.invoice_number) && (
                                        <p className="text-[11px] font-mono font-bold text-slate-500 dark:text-slate-400">
                                            INV: {selectedParticipant.invoice_number || selectedParticipant.latest_payment?.invoice_number}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Grid Identitas Peserta */}
                            <div className="space-y-3">
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Info className="w-4 h-4 text-blue-500" /> Informasi Data Peserta
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/50 dark:bg-slate-800/30 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                                    <div>
                                        <span className="text-xs text-slate-400 font-medium block">Nama Lengkap</span>
                                        <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                                            {selectedParticipant?.full_name || selectedParticipant?.participant?.full_name || "-"}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-400 font-medium block">Email</span>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 break-all">
                                            {selectedParticipant?.email || selectedParticipant?.participant?.email || "-"}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-400 font-medium block">Nomor Telepon / WhatsApp</span>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 font-mono">
                                            {selectedParticipant?.phone_mobile || selectedParticipant?.phone_number || selectedParticipant?.participant?.phone_mobile || "-"}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-400 font-medium block">Jenis Kelamin</span>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 capitalize">
                                            {selectedParticipant?.gender || selectedParticipant?.participant?.gender || "-"}
                                        </p>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <span className="text-xs text-slate-400 font-medium block">Institusi / Instansi</span>
                                        <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 flex items-center gap-1.5">
                                            <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                                            <span>{selectedParticipant?.institution || selectedParticipant?.participant?.institution || "-"}</span>
                                        </p>
                                    </div>
                                    {selectedParticipant?.position && (
                                        <div className="sm:col-span-2">
                                            <span className="text-xs text-slate-400 font-medium block">Jabatan / Profesi</span>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
                                                {selectedParticipant.position}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Grid Informasi PIC (kalau peserta ini didaftarkan kolektif) */}
                            {selectedParticipant?.pic && (
                                <div className="space-y-3">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                        <Users className="w-4 h-4 text-purple-500" /> Didaftarkan oleh PIC (Kolektif)
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-purple-50/50 dark:bg-purple-900/10 p-4 sm:p-5 rounded-2xl border border-purple-100 dark:border-purple-900/30">
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Nama PIC</span>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 flex items-center gap-1.5">
                                                <UserCircle2 className="w-4 h-4 text-purple-500 shrink-0" />
                                                <span>{selectedParticipant.pic.full_name || "-"}</span>
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Email PIC</span>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 break-all flex items-center gap-1.5">
                                                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span>{selectedParticipant.pic.email || "-"}</span>
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Telepon / WhatsApp PIC</span>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 font-mono flex items-center gap-1.5">
                                                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span>{selectedParticipant.pic.phone_mobile || "-"}</span>
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Institusi PIC</span>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 flex items-center gap-1.5">
                                                <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span>{selectedParticipant.pic.institution || "-"}</span>
                                            </p>
                                        </div>
                                        {selectedParticipant.pic.position && (
                                            <div className="sm:col-span-2">
                                                <span className="text-xs text-slate-400 font-medium block">Jabatan PIC</span>
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
                                                    {selectedParticipant.pic.position}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Grid Informasi Paket & Pembayaran */}
                            <div className="space-y-3">
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Package className="w-4 h-4 text-amber-500" /> Detail Paket & Pembayaran
                                </h4>
                                <div className="bg-slate-50/50 dark:bg-slate-800/30 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-3">
                                    <div>
                                        <span className="text-xs text-slate-400 font-medium block">Paket Pilihan</span>
                                        <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 leading-snug">
                                            {selectedParticipant?.package_name || selectedParticipant?.package?.name || "-"}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Harga Paket</span>
                                            <p className="text-sm font-black text-amber-600 dark:text-amber-400 mt-0.5">
                                                {formatRupiah(selectedParticipant?.package_price ?? selectedParticipant?.package?.price ?? 0)}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 font-medium block">Virtual Account / Customer No</span>
                                            <p className="text-sm font-mono font-bold text-slate-800 dark:text-slate-200 mt-0.5">
                                                {selectedParticipant?.latest_payment?.customer_number || "-"}
                                            </p>
                                        </div>
                                    </div>
                                    {selectedParticipant?.notes && (
                                        <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                                            <span className="text-xs text-slate-400 font-medium block">Catatan Pendaftaran</span>
                                            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mt-1 bg-white dark:bg-slate-900 p-3 sm:p-4 rounded-xl border border-slate-200/50 dark:border-slate-800 leading-relaxed">
                                                {selectedParticipant.notes}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <DialogFooter className="shrink-0 p-4 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setSelectedParticipant(null)}
                            className="rounded-xl px-6 font-bold bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                            Tutup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* --- MODAL DETAIL HASIL SYNC PAYMENT GATEWAY --- */}
            <Dialog open={!!syncResult} onOpenChange={(open) => !open && setSyncResult(null)}>
                <DialogContent className="!max-w-[95vw] sm:!max-w-2xl lg:!max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col rounded-[2rem] p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
                    <DialogHeader className="shrink-0 p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 text-left">
                        <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-blue-600" /> Detail Hasil Sync Payment Gateway
                        </DialogTitle>
                        <p className="text-xs text-slate-500 mt-1">
                            {syncResult?.total_va_checked ?? 0} VA dicek, {syncResult?.total_participant_updated ?? 0} peserta diperbarui jadi lunas
                            {(syncResult?.failed_count ?? 0) > 0 ? `, ${syncResult?.failed_count} VA gagal dicek.` : "."}
                        </p>
                    </DialogHeader>

                    <div className="overflow-y-auto flex-1">
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    <th className="py-3 px-4 sm:px-6 w-12">No</th>
                                    <th className="py-3 px-4 sm:px-6">Nomor VA / Nama</th>
                                    <th className="py-3 px-4 sm:px-6 text-center">Status Sebelum</th>
                                    <th className="py-3 px-4 sm:px-6 text-center">Status Sesudah</th>
                                    <th className="py-3 px-4 sm:px-6">Keterangan</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-sm">
                                {(syncResult?.checked_details ?? [])
                                    .slice()
                                    .sort((a, b) => {
                                        // Urutkan: yang baru lunas dulu, lalu yang error, baru sisanya
                                        const rank = (d: SyncCheckedDetail) => (d.updated ? 0 : d.error ? 1 : 2);
                                        return rank(a) - rank(b);
                                    })
                                    .map((detail, idx) => {
                                        const statusBadge = (status?: string | null) => {
                                            if (!status) return <span className="text-slate-400 text-xs">-</span>;
                                            const cls = status === "paid"
                                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                                                : status === "expired"
                                                    ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700"
                                                    : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800";
                                            const label = status === "paid" ? "Lunas" : status === "expired" ? "Kedaluwarsa" : "Belum Lunas";
                                            return (
                                                <Badge className={cn("font-bold px-2.5 py-0.5 shadow-none", cls)}>
                                                    {label}
                                                </Badge>
                                            );
                                        };

                                        return (
                                            <tr key={`${detail.customer_number}-${idx}`} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                                <td className="py-3 px-4 sm:px-6 font-bold text-slate-500 dark:text-slate-400">{idx + 1}</td>
                                                <td className="py-3 px-4 sm:px-6">
                                                    <div className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300">{detail.customer_number}</div>
                                                    <div className="text-xs text-slate-500 mt-0.5">
                                                        {detail.buyer_name || "-"}
                                                        {detail.participant_count > 1 && (
                                                            <span className="text-[10px] text-purple-600 dark:text-purple-400 font-bold ml-1.5">
                                                                (PIC - {detail.participant_count} peserta)
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 sm:px-6 text-center">{statusBadge(detail.previous_status)}</td>
                                                <td className="py-3 px-4 sm:px-6 text-center">{statusBadge(detail.new_status)}</td>
                                                <td className="py-3 px-4 sm:px-6">
                                                    {detail.updated ? (
                                                        <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center gap-1">
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> Diperbarui jadi Lunas
                                                        </span>
                                                    ) : detail.error ? (
                                                        <span className="text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center gap-1">
                                                            <AlertCircle className="w-3.5 h-3.5" /> {detail.error}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400 text-xs">Tidak ada perubahan</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                {(!syncResult?.checked_details || syncResult.checked_details.length === 0) && (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center max-w-sm mx-auto text-slate-400">
                                                <Wallet className="w-10 h-10 mb-3 opacity-20" />
                                                <p className="text-sm font-medium">Tidak ada detail VA untuk ditampilkan.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <DialogFooter className="shrink-0 p-4 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setSyncResult(null)}
                            className="rounded-xl px-6 font-bold bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                            Tutup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

type StatCardProps = {
    label: string;
    value?: number;
    icon: LucideIcon;
    colorClass: "blue" | "emerald" | "rose" | "sky" | "amber";
    isCurrency?: boolean;
};

function StatCard({ label, value, icon: Icon, colorClass, isCurrency = false }: StatCardProps) {
    const colorStyles = {
        blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
        emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
        rose: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400",
        amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
        sky: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
    };

    return (
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col justify-between gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className={`p-2.5 rounded-xl shrink-0 ${colorStyles[colorClass]}`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 truncate">
                    {label}
                </p>
                <p className="text-xl lg:text-2xl font-black text-slate-900 dark:text-white leading-none tracking-tighter break-words">
                    {isCurrency ? formatRupiah(value ?? 0) : (value ?? 0)}
                </p>
            </div>
        </div>
    );
}